<template>
    <v-container fluid class="pa-3">
        <v-card class="pb-10">

            <material-header
                :title="isEditing ? project.name : 'Create new project'"
                :loading="formSaving"
                :info="config.description"
                icon="mdi-folder-clock-outline">

                <template slot="buttons">

                    <v-btn v-if="isEditing" color="secondary" :loading="formSaving" @click="saveChanges()">
                        Update
                    </v-btn>

                    <v-btn v-if="isEditing" class="ml-2" color="primary" :loading="formSaving" @click="saveChanges(true)">
                        Update and close
                    </v-btn>

                    <v-btn v-if="!isEditing" color="secondary" :loading="formSaving" @click="saveChanges()">
                        Save
                    </v-btn>

                    <v-btn v-if="!isEditing" color="primary" class="ml-2" :loading="formSaving" @click="saveChanges(true)">
                        Save and close
                    </v-btn>

                    <v-btn fab color="error" elevation="0" small dark @click="close()" class="ml-3">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>

                </template>
            </material-header>

            <validation-observer ref="parentObserver">

                <v-tabs class="text-left"
                        :show-arrows="false"
                        :vertical="true"
                        slider-color="primary"
                        color="white"
                        v-if="hasPermission('run queries')"
                        v-model="activeTab">

                    <!-- BUG IN VUETIFY FIX -->
                    <v-tab key="tab1" style="width: 250px">
                        BASIC SETTINGS
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab1')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab class="text-left" value="tab2" v-show="!['recontact','router'].includes(form.data.type)">
                        QUALIFICATIONS
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab2')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab class="text-left" value="tab3" v-show="form.data.type==='router'">
                        PARTICIPATION RULES
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab3')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab4" v-show="form.data.type==='recontact'">
                        PANELISTS
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab4')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab5" v-show="hasFeature('balancing') && form.data.type==='auto'">
                        BALANCING
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab5')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab6" v-show="form.data.type!=='router'">
                        INVITATION
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab6')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab7" v-show="form.data.type!=='router'">
                        REMINDER
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab7')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab8">
                        WELCOME PAGE
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab8')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab9" v-show="form.data.type!=='router'">
                        COMPENSATION
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab9')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab10">
                        SURVEY LINKS
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab10')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <v-tab key="tab11" v-if="form.data.type==='router'">
                        SUPPLIERS
                        <v-chip class="ml-2"
                                color="red"
                                small
                                v-if="tabErrors.has('tab11')">
                            ERROR
                        </v-chip>
                    </v-tab>

                    <!-- BASIC SETTINGS -->
                    <v-tab-item eager
                                :value="0"
                                key="tab1"
                                reverse-transition="fade-transition"
                                transition="fade-transition">

                        <basic-tab
                            :project-id="form.data.id"
                            :created-by="form.data.created_by"
                            :loi="form.data.loi"
                            :target-completes="form.data.target_completes"
                            :project-name="form.data.name"
                            :start-date="form.data.start_date"
                            :end-date="form.data.end_date"
                            :status="form.data.status"
                            :type="form.data.type"
                            :categories="form.data.categories"
                            :sampling-priority="form.data.sampling_priority"
                            :client="form.data.client"
                            :assistant-manager="form.data.assistant_manager"
                            :email-notifications-enabled="form.data.email_notifications_enabled"
                            :mobile-friendly="form.data.mobile_friendly"
                            :tablet-friendly="form.data.tablet_friendly"
                            :desktop-friendly="form.data.desktop_friendly"
                            :route-in-enabled="form.data.route_in_enabled"
                            :route-out-enabled="form.data.route_out_enabled"
                            :estimated-incidence-rate="form.data.estimated_incidence_rate"
                            :external-project-reference="form.data.external_project_reference"
                            :external-billing-reference="form.data.external_billing_reference"
                            :error-messages="form.getAllErrors()"
                            :loading="loading"
                            @change="updateFormData"
                            @error="setTabErrorState"
                        ></basic-tab>

                    </v-tab-item>

                    <!-- QUALIFICATIONS -->
                    <v-tab-item eager
                                :value="1"
                                key="tab2"
                                v-show="form.data.mode==='auto'"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <qualifications-tab
                            :json-filter="form.data.json_filter"
                            :json-query="form.data.json_query"
                            :subPanels="subPanels"
                            :target-completes="form.data.target_completes"
                            @change="updateFormData"
                            @error="setTabErrorState"
                            @change:selected-sub-panels="selectedSubPanelsChanged"
                        ></qualifications-tab>
                    </v-tab-item>

                    <!-- PARTICIPATION RULES -->
                    <v-tab-item :value="2"
                                key="tab3"
                                v-show="form.data.type==='router'"
                                reverse-transition="fade-transition"
                                transition="fade-transition">

                        <title-card title="Participation rules" no-padding>

                            <filter-project-config :active="externalSupplierRules.projectFilterActive"
                                                   :operation="externalSupplierRules.projectFilterOperation"
                                                   :project-ids="externalSupplierRules.projectFilterIds"
                                                   :statuses="externalSupplierRules.projectFilterStatuses"
                                                   background-color="transparent"
                                                   :exclude-only="true"
                                                   :disable-statuses="['invited','reminded','opened']"
                                                   @change="participationRulesChanged">
                            </filter-project-config>

                        </title-card>

                    </v-tab-item>

                    <!-- RECONTACT LIST PANELISTS -->
                    <v-tab-item :value="3"
                                key="tab4"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <panelists-tab @change="updateFormData"
                                       @error="setTabErrorState"
                        ></panelists-tab>
                    </v-tab-item>

                    <!-- BALANCING TAB -->
                    <v-tab-item v-if="hasFeature('balancing')"
                                :value="4"
                                key="tab5"
                                v-show="form.data.type==='auto'">
                        <quotas-tab
                            :project="form.data"
                            @change="updateQuotasTab"
                            @change:project="updateFormData"
                            @error="setTabErrorState"
                        ></quotas-tab>
                    </v-tab-item>

                    <!-- INVITATIONS TAB -->
                    <v-tab-item eager
                                :value="5"
                                key="tab6"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <invitation-tab
                            v-if="subPanels.length > 0"
                            :project="projectData"
                            :selected-sub-panels="subPanelFilterIds"
                            @change:translations="updateFormData"
                            @error="setTabErrorState"
                        ></invitation-tab>
                    </v-tab-item>

                    <!-- REMINDER TAB -->
                    <v-tab-item :value="6"
                                key="tab7"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <reminder-tab
                            v-if="subPanels.length > 0"
                            :project="projectData"
                            :selected-sub-panels="subPanelFilterIds"
                            @change:translations="updateFormData"
                            @change:hours="updateFormData"
                            @error="setTabErrorState"
                        ></reminder-tab>
                    </v-tab-item>

                    <!-- WELCOME PAGE TAB -->
                    <v-tab-item :value="7"
                                key="tab8"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <welcome-page-tab
                            v-if="subPanels.length > 0 && projectData"
                            :project="projectData"
                            :selected-sub-panels="subPanelFilterIds"
                            @change="updateFormData"
                            @error="setTabErrorState"
                        ></welcome-page-tab>
                    </v-tab-item>

                    <!-- COMPENSATIONS TAB -->
                    <v-tab-item :value="8"
                                key="tab9"
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <compensation-tab
                            :reward-completed="form.data.reward_completed"
                            :reward-profile="form.data.reward_profile"
                            :reward-quota="form.data.reward_quota"
                            :revenue-completed="form.data.revenue_completed"
                            :revenue-quota="form.data.revenue_quota"
                            :revenue-profile="form.data.revenue_profile"
                            @change="updateFormData"
                            @error="setTabErrorState"
                        ></compensation-tab>
                    </v-tab-item>

                    <!-- LINKS TAB -->
                    <v-tab-item :value="9"
                                key="tab10"
                                v-if="subPanels.length > 0"
                                eager
                                reverse-transition="fade-transition"
                                transition="fade-transition">
                        <links-tab
                            :data-variables="dataVariables"
                            :project="form.data"
                            :error-messages="form.getAllErrors()"
                            :saved="form.data.id ? true : false"
                            @change="updateFormData"
                            @error="setTabErrorState"
                        ></links-tab>
                    </v-tab-item>

                    <!-- EXTERNAL SUPPLIERS TABS -->
                    <v-tab-item :value="10"
                                key="tab11"
                                v-if="subPanels.length > 0 && form.data.type==='router'"
                                reverse-transition="fade-transition"
                                transition="fade-transition">

                        <external-suppliers-tab
                            v-if="project"
                            :project="form.data"
                            :sub-panels="subPanels"
                            :selected-sub-panels="subPanelFilterIds"
                            :error-messages="form.getAllErrors()"
                            @change="updateFormData"
                            @error="setTabErrorState"
                        ></external-suppliers-tab>

                    </v-tab-item>

                </v-tabs>

            </validation-observer>

        </v-card>

        <!-- PROJECT STATS DIALOG -->
        <v-dialog v-if="project"
                  v-model="projectStatsOpenModel"
                  fullscreen
                  hide-overlay
                  transition="fade-transition"
        >
            <stat-boxes :project="project"
                        @close="closeProjectStats"
            ></stat-boxes>

        </v-dialog>

        <json v-if="false" :data="form.data"></json>

    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import Form from '../../../../components/forms/Form';
import ToolbarPage from '../../../../components/UI/ToolbarPage';
import BasicTab from './Tabs/BasicTab.vue';
import SegmentTab from './Tabs/QualificationsTab';
import StatBoxes from '../Dashboard/ProjectDashboard';
import LinksTab from './Tabs/LinksTab';
import QuotasTab from './Tabs/BalancingTab';
import InvitationTab from './Tabs/InvitationTab';
import CompensationTab from './Tabs/CompensationTab';
import ReminderTab from "./Tabs/ReminderTab";
import PanelistsTab from "./Tabs/PanelistsTab";
import moment from 'moment';
import storeMixin from '../StoreMixin';
import {timeDisplay} from '../../../../components/mixins/timeDisplayMixin';
import activeSessionChecker from '../../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../../components/UI/MaterialHeader';
import {mapState, mapActions} from 'vuex';
import WelcomePageTab from "./Tabs/WelcomePageTab";
import QualificationsTab from "./Tabs/QualificationsTab";
import ExternalSuppliers from "./Tabs/SuppliersTab";
import ExternalSuppliersTab from "./Tabs/SuppliersTab";
import FilterProjectConfig from "../../../Segments/components/FilterProjectConfig";
import TitleCard from "../../../../components/UI/TitleCard";

export default {

    mixins: [timeDisplay, storeMixin, activeSessionChecker],

    components: {
        TitleCard,
        FilterProjectConfig,
        ExternalSuppliersTab,
        ExternalSuppliers,
        QualificationsTab,
        ToolbarPage,
        Json,
        StatBoxes,
        BasicTab,
        SegmentTab,
        LinksTab,
        QuotasTab,
        InvitationTab,
        CompensationTab,
        ReminderTab,
        MaterialHeader,
        PanelistsTab,
        WelcomePageTab
    },

    props: {
        project: {
            required: false,
            type: Object,
            default: () => ({
                id: null,
                name: 'My project',
                status: 'offline',
                target_completes: 1500,
                type: 'auto',
                start_date: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
                end_date: moment().utc().add(7, 'days').format('YYYY-MM-DD HH:mm:ss'),
                categories: [],
                json_query: {
                    "booleanOperation": "must",
                    "conditions": [
                        {
                            "operation": "match"
                        }
                    ]
                },
                json_filter: {},
                sampling_priority: 5,
                exit_link_algorithm: 'None',
                exit_link_secret: '!Secret!',
                email_notifications_enabled: true,
                assistant_manager: null,
                mobile_friendly: true,
                table_friendly: true,
                desktop_friendly: true,
                client: null,
                created_by: null,
                loi: 15,
                estimated_incidence_rate: 20,
                revenue_completed: 300,
                revenue_profile: 0,
                revenue_quota: 0,
                reward_completed: 100,
                reward_profile: 0,
                reward_quota: 0,
                email_translations: null,
                reminder_translations: null,
                reminder_hours: null,
                welcome_translations: null,
                welcome_enabled: true,
                entry_link: {
                    base_url: null,
                    example_url: null,
                    params: []
                },
                recontact_identifiers: [],
                quotas: [],
                quotas_enabled: false,
                route_in_enabled: false,
                route_out_enabled: false,
                project_suppliers: [],
                external_project_reference: null,
                external_billing_reference: null,
                external_supplier_rules: {
                    projectFilterActive: false,
                    projectFilterIds: [],
                    projectFilterStatuses: ['completed'],
                    projectFilterOperation: 'must_not'
                }
            })
        }
    },

    data() {
        return {
            isEditing: false,
            loading: false,
            transition: 'scale-transition',
            form: null,
            formValid: false,
            displayCompletesCount: false,
            formSaving: false,
            activeTab: 0,
            projectStatsOpenModel: false,
            selectedSubPanels: null,
            tabErrors: null,

            tabConfig: [
                {
                    index: 0,
                    description: 'Basic settings',
                },
                {
                    index: 1,
                    description: 'Configure qualifications',
                },
                {
                    index: 2,
                    description: 'Participation rules'
                },
                {
                    index: 3,
                    description: 'Recontact panelists using identifiers',
                },
                {
                    index: 4,
                    description: 'Configure how sample should be balanced',
                },
                {
                    index: 5,
                    description: 'Customize the invitation email',
                },
                {
                    index: 6,
                    description: 'Customize the reminder email',
                },
                {
                    index: 7,
                    description: 'Customize project\'s welcome page'
                },
                {
                    index: 8,
                    description: 'Decide compensation for this project',
                },
                {
                    index: 9,
                    description: 'Configure survey links',
                },
                {
                    index: 10,
                    description: 'Manage external suppliers'
                },

            ]
        }
    },

    computed: {

        ...mapState([
            'dataVariables'
        ]),

        ...mapState('PanelSettings', [
            'settings'
        ]),

        ...mapState('SubPanels', [
            'subPanels'
        ]),

        projectData() {
            if (this.form.data) {
                return this.form.data;
            } else {
                return this.project;
            }
        },

        subPanelFilterActive() {
            let filter = this.form.data.json_filter;
            if (filter.includeSubpanelsActive) {
                return filter.includeSubpanelsActive;
            }
            return false;
        },

        subPanelFilterIds() {
            let filter = this.form.data.json_filter;
            if (filter.includeSubpanels) {
                return filter.includeSubpanels;
            }
            return [];
        },

        config() {
            let hint = this.tabConfig.find(hint => {
                return hint.index === this.activeTab;
            });
            if (!hint) {
                throw new Error('Could not locate tab config');
            }
            return hint;
        },

        externalSupplierRules() {
            if (this.form.external_supplier_rules) return this.form.external_supplier_rules;

            return {
                projectFilterActive: false,
                projectFilterIds: [],
                projectFilterStatuses: ['completed'],
                projectFilterOperation: 'must_not'
            }
        }
    },

    created() {

        this.tabErrors = new Set();

        if (this.$route.params.id) {

            this.isEditing = true;

            if (!this.$route.params.project) {
                this.loadFromStore(this.$route.params.id);
            } else {
                this.prepareProject(this.$route.params.project);
            }

        } else {
            this.isEditing = false;
            this.createNewProject()
        }

        this.loadSubPanels()
            .catch(() => {
                Bus.$emit('flash-message', {
                    type: 'error',
                    message: 'OOPS. We failed to required data! Please try again'
                });
            });

        this.getDataVariables()
            .catch(() => {
                Bus.$emit('flash-message', {
                    type: 'error',
                    message: 'OOPS. We failed to required data! Please try again'
                });
            })
    },

    watch: {
        project: function (project) {
            this.form = new Form(project);
        },

        settings(settings) {
            if (!this.form.data.id && settings.revenue_completed) {
                this.setDefaultCompensation();
            }
        }
    },

    methods: {

        ...mapActions([
            'getDataVariables',
        ]),

        ...mapActions('SubPanels', [
            'loadSubPanels'
        ]),

        ...mapActions('ProjectManager', [
            'getProject',
            'updateProject',
            'createProject'
        ]),

        selectedSubPanelsChanged(ids) {
            this.selectedSubPanels = ids;
        },

        setTabErrorState(state) {

            if (state.error) {
                this.tabErrors.add(state.name);
            } else {
                this.tabErrors.delete(state.name);
            }
        },

        loadFromStore(id) {

            this.loading = true;

            this.form = new Form(this.project);

            this.getProject({id: id, force: false})

                .then((project) => {
                    this.prepareProject(project);
                })
                .catch((error) => {
                    console.log(error);
                    Bus.$emit('flash-message', {type: 'error', message: 'Project failed to load!'});
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        prepareProject(project) {

            // PHP to JS conversion for empty object comes across
            // as empty array. If so convert to empty object automatically
            if (Array.isArray(project.json_filter)) {
                project.json_filter = {};
            }

            // If field is null add sensible defaults
            if (project.external_supplier_rules === null) {

                project.external_supplier_rules = {
                    projectFilterActive: false,
                    projectFilterOperation: 'must not',
                    projectFilterStatuses: ['completed'],
                    projectFilterIds: [],
                }
            }

            this.form = new Form(project);

            const filter = project.json_filter;

            // Get sub panels from the query and save for all the components
            // to use with like invitations that need to know locales needed.
            if (filter && filter.includeSubpanels && filter.includeSubpanels.length > 0) {
                this.selectedSubPanels = filter.includeSubpanels;
            }
        },

        setDefaultCompensation() {
            // Use defaults configured in the panel settings
            this.form.data.revenue_completed = this.settings.revenue_completed;
            this.form.data.revenue_quota = this.settings.revenue_quota;
            this.form.data.revenue_profile = this.settings.revenue_profile;
            this.form.data.reward_completed = this.settings.reward_completed;
            this.form.data.reward_quota = this.settings.reward_quota;
            this.form.data.reward_profile = this.settings.reward_profile;
        },

        createNewProject() {

            let newProject = Object.assign({}, this.project);

            newProject.created_by = {
                id: this.$store.state.user.id,
                name: this.$store.state.user.name,
                photo_url: this.$store.state.user.photo_url
            };

            this.form = new Form(newProject);
        },

        close() {
            this.$router.go(-1);
        },

        async saveChanges(close) {

            if (this.$refs.parentObserver) {

                this.$refs.parentObserver.validate()
                    .then(isValid => {
                        if (!isValid) {

                            const errors = Object.values(this.$refs.parentObserver.errors);
                            const firstError = errors.find(error => error.length > 0)[0];

                            Bus.$emit('flash-message', {type: 'error', message: firstError});
                            return;
                        }

                        if (!this.isMinimumSetup()) {
                            return;
                        }

                        // Remove external supplier rules if not enabled
                        if (this.form.data.external_supplier_rules && !this.form.data.external_supplier_rules.projectFilterActive) {
                            this.form.data.external_supplier_rules = null;
                        }

                        this.formSaving = true;

                        if (this.form.data.id) {

                            this.updateProject(this.form)
                                .then(() => {
                                    Bus.$emit('flash-message', {message: 'Project updated...'});

                                    this.formSaving = false;

                                    if (close) {
                                        this.$router.go(-1);
                                    }

                                })
                                .catch((error) => {

                                    if (error.status === 422) {

                                        const errors = Object.values(error.data.errors);
                                        const firstError = errors.find(error => error.length > 0)[0];

                                        Bus.$emit('flash-message', {type: 'error', message: firstError});
                                    } else {
                                        Bus.$emit('flash-message', {
                                            type: 'error',
                                            message: 'OOPS! Project failed to update!'
                                        });
                                    }
                                    this.formSaving = false;
                                });
                        } else {

                            delete (this.form.data.id);

                            this.createProject(this.form)
                                .then((response) => {

                                    this.form.data = Object.assign({}, response.data.project);
                                    this.isEditing = true;

                                    Bus.$emit('flash-message', {message: 'Project saved...'});
                                    this.formSaving = false;

                                    if (close) {
                                        this.$router.go(-1);
                                    }
                                })
                                .catch((error) => {

                                    if (error.status === 422) {

                                        const errors = Object.values(error.data.errors);
                                        const firstError = errors.find(error => error.length > 0)[0];

                                        Bus.$emit('flash-message', {type: 'error', message: firstError});
                                    } else {
                                        Bus.$emit('flash-message', {
                                            type: 'error',
                                            message: 'OOPS! Project failed to save!'
                                        });
                                    }
                                    this.formSaving = false;
                                });
                        }

                    });
            }
        },

        isMinimumSetup() {
            if (this.form.data.type === "auto") {
                return this.isQualificationSetup()
            }
            return true;
        },

        isQualificationSetup() {
            const conditionFound = this.form.data.json_query.conditions.find(condition => condition.dataVariableId);

            if (!conditionFound) {
                Bus.$emit('flash-message', {type: 'error', message: 'Your must define qualifications before you can save this project'});
            }
            return !!conditionFound;
        },

        openProjectStats() {
            this.projectStatsOpenModel = true;
        },

        closeProjectStats() {
            this.projectStatsOpenModel = false;
        },

        updateQuotasTab(data) {
            if (data.quotas.length === 0) {
                this.form.data.quotas_enabled = false;
            }
            this.updateFormData(data);
        },

        updateFormData(data) {
            this.form.data = Object.assign(this.form.data, data);
        },

        participationRulesChanged(rules) {
            console.log(rules);
            this.form.data.external_supplier_rules = rules;
        }
    }
}
</script>

